import { useCallback, useEffect, useRef, useState } from 'react';
import type { FilterItem } from '@wconcept/wconcept-common/models/renewal/common';
import type {
  LpProductCountResult,
  LpProductFilterResult,
  LpProductFilterType,
  LpProductFilters,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import { isInstanceOfAPIError } from '@utils/customErrors';
import {
  getLpProductV2FilterType,
  getLpProductV2FiltersWithUpdatedCounts,
  getLpProductV2Request,
  mergeLpProductV2FilterResult,
} from '../filterV2';

interface UseLpFilterModalDataProps {
  readonly tab: string;
  readonly brandTab: string;
  readonly selectedFilters: readonly FilterItem[];
  readonly originalFilters: LpProductFilters;
  readonly requestFilter: (
    type: LpProductFilterType,
    params: LpProductRequest
  ) => Promise<LpProductFilterResult | undefined>;
  readonly requestCount: (
    params: LpProductRequest
  ) => Promise<LpProductCountResult | undefined>;
}

interface UseLpFilterModalDataResult {
  readonly filters: LpProductFilters;
  readonly total: number;
  readonly isLoading: boolean;
  readonly isNetworkError: boolean;
  readonly isServiceError: boolean;
  readonly retryRequests: () => void;
}

export const useLpFilterModalData = ({
  tab,
  brandTab,
  selectedFilters,
  originalFilters,
  requestFilter,
  requestCount,
}: UseLpFilterModalDataProps): UseLpFilterModalDataResult => {
  const [filters, setFilters] = useState(originalFilters);
  const [total, setTotal] = useState(0);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isServiceError, setIsServiceError] = useState(false);
  const filterRequestIdRef = useRef(0);
  const countRequestIdRef = useRef(0);

  const handleRequestError = useCallback((error: unknown): void => {
    const isOffline = isInstanceOfAPIError(error) && error.status === 513;

    setIsNetworkError(isOffline);
    setIsServiceError(!isOffline);
  }, []);

  const updateFilter = useCallback(async (): Promise<void> => {
    const filterType = getLpProductV2FilterType(
      tab === 'topBrand' ? brandTab : tab
    );
    if (!filterType) return;

    filterRequestIdRef.current += 1;
    const requestId = filterRequestIdRef.current;
    setIsFilterLoading(true);
    setIsNetworkError(false);
    setIsServiceError(false);

    try {
      const result = await requestFilter(
        filterType,
        getLpProductV2Request(selectedFilters)
      );

      if (requestId === filterRequestIdRef.current && result) {
        const updatedFilters = getLpProductV2FiltersWithUpdatedCounts(
          originalFilters,
          result.filters,
          selectedFilters
        );

        setFilters(currentFilters =>
          mergeLpProductV2FilterResult(
            currentFilters,
            updatedFilters,
            filterType
          )
        );
      }
    } catch (error) {
      if (requestId === filterRequestIdRef.current) {
        handleRequestError(error);
      }
    } finally {
      if (requestId === filterRequestIdRef.current) {
        setIsFilterLoading(false);
      }
    }
  }, [
    brandTab,
    handleRequestError,
    originalFilters,
    requestFilter,
    selectedFilters,
    tab,
  ]);

  const updateCount = useCallback(async (): Promise<void> => {
    countRequestIdRef.current += 1;
    const requestId = countRequestIdRef.current;
    setIsCountLoading(true);
    setIsNetworkError(false);
    setIsServiceError(false);

    try {
      const result = await requestCount(getLpProductV2Request(selectedFilters));

      if (requestId === countRequestIdRef.current && result) {
        const { total: nextTotal } = result;
        setTotal(nextTotal);
      }
    } catch (error) {
      if (requestId === countRequestIdRef.current) {
        handleRequestError(error);
      }
    } finally {
      if (requestId === countRequestIdRef.current) {
        setIsCountLoading(false);
      }
    }
  }, [handleRequestError, requestCount, selectedFilters]);

  const retryRequests = useCallback((): void => {
    updateFilter();
    updateCount();
  }, [updateCount, updateFilter]);

  useEffect(() => {
    updateFilter();
  }, [updateFilter]);

  useEffect(() => {
    updateCount();
  }, [updateCount]);

  return {
    filters,
    total,
    isLoading: isFilterLoading || isCountLoading,
    isNetworkError,
    isServiceError,
    retryRequests,
  };
};
